import type {
  CaixaBanco,
  ContaBancariaCaixa,
  ParticipanteCaixa,
  PrestadorCaixa,
  ParceiroCaixa,
  SaqueCaixa,
  LancamentoExtratoCaixa,
} from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Resultado } from '../../core/resultado.js';
import { MARCA_PG } from '../../core/app-shell/marca.js';
import type { CaixaBancoApi } from './caixa-banco.api.js';
import type { PermissoesFaturamento } from '../faturamento/faturamento.permissoes.js';

function brl(c: number | null): string {
  return c === null ? '—' : formatBRL(centavosParaReais(c));
}
function dataCurta(iso: string | null): string {
  return iso ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(iso)) : '—';
}
function esc(v: string): string {
  const d = document.createElement('div');
  d.textContent = v;
  return d.innerHTML;
}

const ROTULO_ESTADO_SAQUE: Record<SaqueCaixa['estado'], string> = {
  solicitada: 'Solicitada',
  em_analise: 'Em análise',
  aprovada: 'Aprovada',
  rejeitada: 'Negada',
  paga: 'Paga',
  cancelada: 'Cancelada',
};

/**
 * `<erp-caixa-banco-page>` — "Financeiro / Caixa Banco" (referência
 * visual `pg_custom_caixa_banco_prototipo`). Só leitura. Contas e saldos
 * vêm do livro-razão real (`financeiro.contas`/`transacoes`). Seções de
 * participantes/prestadores/parceiros/extratos/saques leem as tabelas
 * reais do ERP — quando ainda não há registro, mostram estado honesto
 * ("nenhum registro"), nunca os números do protótipo.
 */
export class ErpCaixaBancoPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _api: CaixaBancoApi | null = null;
  private _permissoes: PermissoesFaturamento = { ler: false };
  private resultado: Resultado<CaixaBanco> = { status: 'carregando' };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set caixaBancoApi(valor: CaixaBancoApi) {
    this._api = valor;
    if (this.isConnected) void this.carregar();
  }
  set permissoes(valor: PermissoesFaturamento) {
    this._permissoes = valor;
    if (this.isConnected) this.render();
  }

  connectedCallback(): void {
    this.render();
    if (this._api) void this.carregar();
  }

  private async carregar(): Promise<void> {
    if (!this._api) return;
    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.render();
      return;
    }
    this.resultado = { status: 'carregando' };
    this.render();
    this.resultado = await this._api.obter();
    this.render();
  }

  private render(): void {
    if (!this._permissoes.ler) {
      this.root.innerHTML = `<style>${CSS}</style><div class="nota sem-acesso">Você não tem permissão para ver o Caixa Banco (recurso <b>financeiro</b>).</div>`;
      return;
    }
    if (this.resultado.status === 'carregando') {
      this.root.innerHTML = `<style>${CSS}</style>${this.headerHtml()}<div class="estado">Carregando Caixa Banco…</div>`;
      return;
    }
    if (this.resultado.status === 'erro') {
      this.root.innerHTML = `<style>${CSS}</style>${this.headerHtml()}<div class="estado erro">${esc(this.resultado.mensagem)}</div>`;
      this.root.querySelector('[data-acao="atualizar"]')?.addEventListener('click', () => void this.carregar());
      return;
    }
    if (this.resultado.status !== 'ok') {
      this.root.innerHTML = `<style>${CSS}</style>${this.headerHtml()}<div class="estado">Sem dados.</div>`;
      return;
    }

    const cb = this.resultado.dado;
    const creditosMembros =
      soma(cb.participantes.map((p) => p.saldoCentavos)) +
      soma(cb.prestadores.map((p) => p.saldoPendenteCentavos + p.saldoPagoCentavos)) +
      soma(cb.parceiros.map((p) => p.saldoPendenteCentavos + p.saldoPagoCentavos));

    this.root.innerHTML = `
      <style>${CSS}</style>
      ${this.headerHtml()}

      <div class="kpis">
        <div class="kpi"><div class="lab">Saldo consolidado</div><div class="num">${brl(cb.saldoTotalBancosCentavos)}</div><div class="muted">Contas de banco / recebíveis</div></div>
        <div class="kpi"><div class="lab">CC PG Custom</div><div class="num ouro">${brl(cb.ccPgCustomCentavos)}</div><div class="muted">${cb.ccPgCustomCentavos === null ? 'Fórmula a definir (não é o saldo bancário)' : 'Lucros disponíveis / acumulados'}</div></div>
        <div class="kpi"><div class="lab">Créditos dos membros</div><div class="num">${brl(creditosMembros)}</div><div class="muted">Lucros + serviços + comissões</div></div>
        <div class="kpi"><div class="lab">Diferença dos bancos</div><div class="num verde">${brl(cb.diferencaBancosCentavos)}</div><div class="muted">${cb.conciliacaoAutomatica ? 'Conciliação' : 'Conciliação automática não configurada'}</div></div>
      </div>

      <section class="sec">
        <div class="sectop"><div><h2>Contas bancárias e recebíveis</h2><div class="muted">Cada conta separada • saldo do livro-razão • sem extrato bancário externo</div></div></div>
        <div class="cards4">${cb.contas.map(cardConta).join('')}</div>
        <div class="tabela-wrap">
          <table>
            <thead><tr><th>CONTA</th><th>TIPO</th><th>SALDO</th><th>ENTRADAS</th><th>SAÍDAS</th><th>ÚLTIMA MOVIMENTAÇÃO</th><th>STATUS</th></tr></thead>
            <tbody>${cb.contas.map(linhaConta).join('')}</tbody>
          </table>
        </div>
        <div class="cards2 mt">
          <div class="kpi"><div class="lab">Saldo total dos bancos</div><div class="num">${brl(cb.saldoTotalBancosCentavos)}</div><div class="muted">Soma das contas de banco / recebíveis</div></div>
          <div class="kpi"><div class="lab">Diferença dos bancos</div><div class="num verde">${brl(cb.diferencaBancosCentavos)}</div><div class="muted">Saldo consolidado × soma das contas</div></div>
        </div>
      </section>

      <section class="sec">
        <div class="sectop"><div><h2 class="rosa">CC PG Custom</h2><div class="muted">Conta interna de créditos • não é saldo bancário</div></div></div>
        <div class="cards2">
          <div class="kpi"><div class="lab">Saldo da empresa</div><div class="num ouro">${brl(cb.ccPgCustomCentavos)}</div><div class="muted">${cb.ccPgCustomCentavos === null ? 'A fórmula de lucro disponível/acumulado ainda não foi definida pelo owner — nenhum número é mostrado até lá' : 'Base interna para créditos de participação'}</div></div>
          <div class="kpi"><div class="lab">Saques pendentes</div><div class="num">${brl(cb.saquesPendentesCentavos)}</div><div class="muted">Solicitações aguardando decisão</div></div>
        </div>
      </section>

      <section class="sec">
        <div class="sectop"><div><h2>Participação — lucros</h2><div class="muted">Percentuais vigentes (societario.participacoes_societarias)</div></div></div>
        ${
          cb.participantes.length === 0
            ? estadoVazio(
                'Nenhuma participação societária cadastrada no ERP',
                'Os percentuais e créditos dos sócios vivem hoje no sistema do site (/adm). A ponte de leitura ERP→site vai trazer esses dados — ainda não ativada.'
              )
            : `<div class="cards3">${cb.participantes.map(cardParticipante).join('')}</div>`
        }
      </section>

      <section class="sec">
        <div class="sectop"><div><h2>Prestadores de serviços</h2><div class="muted">Créditos originados de serviços prestados (comissoes.comissoes tipo técnico)</div></div></div>
        ${
          cb.prestadores.length === 0
            ? estadoVazio(
                'Nenhum crédito de prestador registrado no ERP',
                'A remuneração de serviço (Pastor Mauro, Latway) é calculada hoje no sistema do site. A regra de % nunca é assumida aqui — vem de comissoes.regras quando cadastrada.'
              )
            : `<div class="cards2">${cb.prestadores.map(cardPrestador).join('')}</div>`
        }
      </section>

      <section class="sec">
        <div class="sectop"><div><h2>Parceiros</h2><div class="muted">Comissão sobre vendas atribuídas pelo cupom</div></div></div>
        ${
          cb.parceiros.length === 0
            ? estadoVazio('Nenhum parceiro com comissão no ERP', 'As comissões aparecem conforme pedidos pagos com cupom de parceiro são sincronizados.')
            : `<div class="tabela-wrap"><table>
                <thead><tr><th>PARCEIRO</th><th>REGRA</th><th>PENDENTE</th><th>PAGO</th><th>VENDAS ATRIBUÍDAS</th></tr></thead>
                <tbody>${cb.parceiros.map(linhaParceiro).join('')}</tbody>
              </table></div>`
        }
      </section>

      <section class="sec">
        <div class="sectop"><div><h2 class="rosa">Extrato / Saques</h2><div class="muted">Cada membro tem uma conta interna e histórico auditável</div></div></div>
        ${
          cb.extrato.length === 0 && cb.saques.length === 0
            ? estadoVazio('Nenhuma movimentação registrada', 'Créditos de carteira e solicitações de saque aparecem aqui conforme acontecem.')
            : `<div class="tabela-wrap"><table>
                <thead><tr><th>DATA</th><th>MEMBRO</th><th>TIPO</th><th>DESCRIÇÃO</th><th>CRÉDITO</th><th>DÉBITO</th><th>STATUS</th></tr></thead>
                <tbody>${cb.extrato.map(linhaExtrato).join('')}</tbody>
              </table></div>
              ${cb.saques.length > 0 ? `<div class="tabela-wrap mt"><table>
                <thead><tr><th>SOLICITADA EM</th><th>MEMBRO</th><th>VALOR</th><th>ESTADO</th><th>PAGA EM</th></tr></thead>
                <tbody>${cb.saques.map(linhaSaque).join('')}</tbody>
              </table></div>` : ''}`
        }
      </section>

      <div class="nota">
        <b>Regra de integridade:</b> todos os valores vêm do banco real, em centavos.
        Saldos de conta são o livro-razão interno de cada gateway — <b>não</b> um extrato
        bancário conciliado (não há integração com API de banco). Nenhum saque altera o
        saldo definitivamente antes da aprovação: negado devolve o valor por construção.
        CC PG Custom não é o saldo bancário e não mostra número até a fórmula ser definida.
      </div>
    `;

    this.root.querySelector('[data-acao="atualizar"]')?.addEventListener('click', () => void this.carregar());
  }

  private headerHtml(): string {
    return `
      <header class="head">
        <div class="head-id">
          <img class="head-emblema pink" src="${MARCA_PG.emblemaRosa}" alt="" aria-hidden="true" />
          <div>
            <h1>Caixa Banco</h1>
            <div class="muted">Saldos bancários, caixa interno, créditos e saques</div>
          </div>
        </div>
        <div class="acoes"><button class="btn primario" data-acao="atualizar">Atualizar</button></div>
      </header>`;
  }
}

function soma(ns: number[]): number {
  return ns.reduce((a, b) => a + b, 0);
}

function statusConta(c: ContaBancariaCaixa): { texto: string; classe: string } {
  if (c.origemDados === 'sem_integracao') return { texto: 'AGUARDANDO CONEXÃO', classe: 'pendente' };
  const temMov = c.entradasCentavos !== 0 || c.saidasCentavos !== 0;
  return temMov ? { texto: 'CONECTADO', classe: 'ok' } : { texto: 'SEM MOVIMENTO', classe: 'alerta' };
}

function cardConta(c: ContaBancariaCaixa): string {
  const s = statusConta(c);
  const tipo = c.tipo === 'conta_corrente' ? 'CONTA CORRENTE' : c.tipo === 'caixa' ? 'CAIXA INTERNO' : 'RECEBÍVEIS';
  return `<div class="kpi"><div class="lab">${esc(c.nome.toUpperCase())}</div><div class="num">${brl(c.saldoCentavos)}</div><div class="muted">${tipo}</div><div class="mt-s"><span class="badge ${s.classe}">${s.texto}</span></div></div>`;
}
function linhaConta(c: ContaBancariaCaixa): string {
  const s = statusConta(c);
  const tipo = c.tipo === 'conta_corrente' ? 'Conta corrente' : c.tipo === 'caixa' ? 'Caixa interno' : 'Recebíveis';
  return `<tr>
    <td><b>${esc(c.nome)}</b></td><td>${tipo}</td>
    <td><b>${brl(c.saldoCentavos)}</b></td>
    <td class="verde">${brl(c.entradasCentavos)}</td>
    <td class="vermelho">${brl(c.saidasCentavos)}</td>
    <td>${dataCurta(c.ultimaMovimentacao)}</td>
    <td><span class="badge ${s.classe}">${s.texto}</span></td>
  </tr>`;
}
function cardParticipante(p: ParticipanteCaixa): string {
  return `<div class="kpi">
    <div class="person"><b>${esc(p.nome)}</b><b class="ouro">${p.percentualSocietario !== null ? `${p.percentualSocietario}%` : 'A definir'}</b></div>
    <div class="num sm">${brl(p.saldoCentavos)}</div><div class="muted">Saldo atual em carteira</div>
  </div>`;
}
function cardPrestador(p: PrestadorCaixa): string {
  return `<div class="kpi">
    <div class="person"><b>${esc(p.nome)}</b><b class="ouro">${p.percentualServico !== null ? `${p.percentualServico}%` : 'Regra não definida'}</b></div>
    <div class="num sm">${brl(p.saldoPendenteCentavos)}</div><div class="muted">Pendente • pago ${brl(p.saldoPagoCentavos)}</div>
  </div>`;
}
function linhaParceiro(p: ParceiroCaixa): string {
  return `<tr><td><b>${esc(p.nome)}</b></td><td>${esc(p.regra)}</td><td>${brl(p.saldoPendenteCentavos)}</td><td>${brl(p.saldoPagoCentavos)}</td><td>${p.vendasAtribuidas}</td></tr>`;
}
function linhaExtrato(l: LancamentoExtratoCaixa): string {
  return `<tr>
    <td>${dataCurta(l.data)}</td><td>${esc(l.membro ?? '—')}</td><td>${esc(l.tipo.toUpperCase())}</td>
    <td>${esc(l.descricao)}</td>
    <td class="verde">${l.creditoCentavos !== null ? brl(l.creditoCentavos) : '—'}</td>
    <td class="vermelho">${l.debitoCentavos !== null ? brl(l.debitoCentavos) : '—'}</td>
    <td><span class="badge neutro">${esc(l.status.toUpperCase())}</span></td>
  </tr>`;
}
function linhaSaque(s: SaqueCaixa): string {
  return `<tr><td>${dataCurta(s.solicitadaEm)}</td><td>${esc(s.membro)}</td><td><b>${brl(s.valorCentavos)}</b></td><td><span class="badge ${s.estado === 'paga' ? 'ok' : s.estado === 'rejeitada' || s.estado === 'cancelada' ? 'vermelho-badge' : 'pendente'}">${ROTULO_ESTADO_SAQUE[s.estado]}</span></td><td>${dataCurta(s.pagaEm)}</td></tr>`;
}
function estadoVazio(titulo: string, descricao: string): string {
  return `<div class="vazio"><b>${esc(titulo)}</b><span>${esc(descricao)}</span></div>`;
}

const CSS = `
  :host { display: block; font-family: var(--pg-font-familia-base, system-ui); color: var(--pg-color-texto-primario, #f2f3f5); }
  .muted { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: 11px; }
  .head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
  .head-id { display: flex; align-items: center; gap: 12px; }
  .head-emblema { width: 34px; height: 34px; object-fit: contain; }
  .head-emblema.pink { filter: drop-shadow(0 0 7px rgba(224,95,155,0.22)); }
  h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: 1.5rem; margin: 0 0 2px; }
  h2 { font-family: var(--pg-font-familia-display, system-ui); font-size: 1.05rem; margin: 0 0 2px; }
  h2.rosa { color: var(--pg-color-rosa-pg-art, #e05f9b); }
  .acoes { display: flex; gap: 8px; }
  .btn { border: 1px solid var(--pg-color-borda, #2a2f36); background: var(--pg-color-bg-elevado, #1a1d21); color: inherit; border-radius: 8px; padding: 9px 14px; font-size: 12px; font-weight: 600; cursor: pointer; }
  .btn.primario { background: linear-gradient(135deg, var(--pg-color-dourado, #c9a24b), #9c7c33); border-color: transparent; color: #14100a; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
  .cards4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 12px; }
  .cards3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .cards2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  @media (max-width: 950px) { .kpis, .cards4, .cards3, .cards2 { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 560px) { .kpis, .cards4, .cards3, .cards2 { grid-template-columns: 1fr; } }
  .mt { margin-top: 12px; } .mt-s { margin-top: 10px; }
  .kpi { background: var(--pg-color-bg-elevado, #171b20); border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 10px; padding: 16px; }
  .kpi .lab { font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--pg-color-texto-secundario, #89929e); }
  .kpi .num { font-size: 1.5rem; font-weight: 800; margin: 6px 0 2px; }
  .kpi .num.sm { font-size: 1.25rem; }
  .person { display: flex; justify-content: space-between; gap: 10px; align-items: baseline; }
  .ouro { color: var(--pg-color-dourado, #c9a24b); }
  .verde { color: var(--pg-color-sucesso, #3bb273); }
  .vermelho { color: var(--pg-color-erro, #e5484d); }
  .sec { margin-top: 24px; }
  .sectop { margin-bottom: 12px; }
  .tabela-wrap { overflow-x: auto; border: 1px solid var(--pg-color-borda, #2a2f36); border-radius: 10px; background: var(--pg-color-bg-elevado, #15191d); }
  table { border-collapse: collapse; width: 100%; min-width: 720px; }
  th, td { padding: 11px 13px; border-bottom: 1px solid var(--pg-color-borda, #252a31); text-align: left; white-space: nowrap; }
  th { font-size: 10px; letter-spacing: 0.06em; color: var(--pg-color-texto-secundario, #7f8996); font-weight: 800; }
  td { font-size: 12px; }
  tr:last-child td { border-bottom: 0; }
  .badge { display: inline-block; padding: 4px 8px; border-radius: 20px; font-size: 10px; font-weight: 800; }
  .badge.ok { color: var(--pg-color-sucesso, #3bb273); background: rgba(59,178,115,0.14); }
  .badge.alerta { color: var(--pg-color-alerta, #e0a52c); background: rgba(224,165,44,0.14); }
  .badge.pendente { color: #5ca4ff; background: rgba(92,164,255,0.14); }
  .badge.neutro { color: var(--pg-color-texto-secundario, #9aa1ab); background: rgba(154,161,171,0.12); }
  .badge.vermelho-badge { color: var(--pg-color-erro, #e5484d); background: rgba(229,72,77,0.14); }
  .vazio { display: flex; flex-direction: column; gap: 4px; border: 1px dashed var(--pg-color-borda, #2a2f36); border-radius: 10px; padding: 18px; }
  .vazio b { font-size: 13px; } .vazio span { font-size: 11px; color: var(--pg-color-texto-secundario, #9aa1ab); line-height: 1.6; }
  .estado { padding: 40px 16px; text-align: center; color: var(--pg-color-texto-secundario, #9aa1ab); font-size: 13px; }
  .estado.erro { color: var(--pg-color-erro, #e5484d); }
  .nota { margin-top: 22px; border: 1px solid rgba(224,95,155,0.25); background: rgba(224,95,155,0.05); color: var(--pg-color-texto-secundario, #b9b09a); border-radius: 8px; padding: 12px 14px; font-size: 11px; line-height: 1.7; }
  .nota.sem-acesso { border-color: var(--pg-color-borda, #2a2f36); background: var(--pg-color-bg-elevado, #1a1d21); }
`;

customElements.define('erp-caixa-banco-page', ErpCaixaBancoPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-caixa-banco-page': ErpCaixaBancoPage;
  }
}
